import React, { useEffect, useMemo, useState } from 'react'
import './FaxStyle.css'
import useFaxSettings, { type FaxNumberRow } from './useFax'
import SettingsSkeleton from '@pages/Settings/SettingsSkeleton.page'
import useCommunicationsLeaveGuard from '../shared/useCommunicationsLeaveGuard'
import PortFaxModal from './PortNumber/PortFaxModal.component'
import PurchaseFaxModal from './PurchaseNumber/PurchaseFaxModal.component'
import {
  isSettingFieldChanged,
  settingsFieldClassName,
} from '@utils/settings.util'
import FaxDeleteNumberModal from './FaxDeleteNumberModal.component'
import {
  ActionButtons,
  Badge,
  Button,
  DataTable,
  EmptyState,
  InputField,
  InputWithUnit,
  SearchWithFilter,
  Select,
  ToggleSwitch,
  type BadgeColor,
  type DataTableColumn,
  type DataTableSortDirection,
} from '@enamelai/design-system'
import { Icon as DeleteFilledIcon } from '@enamelai/design-system/icons/Delete2'
import { Icon as FaxIcon } from '@enamelai/design-system/icons/Fax'

type FaxTableRow = { id: string }

type FaxSortColumn = 'name' | 'fax_number' | 'assigned_to' | 'status'

const DEFAULT_PAGE_SIZE = 10

const RETENTION_UNITS = [
  'day/s',
  'week/s',
  'month/s',
  'year/s',
  'No expiration',
]

const SHOW_IMAGE_OPTIONS = [
  { label: 'Ask before displaying', value: 'ask' },
  { label: 'Always display', value: 'always' },
  { label: 'Never display', value: 'never' },
]

const selectValue = (value: string | string[]): string =>
  Array.isArray(value) ? (value[0] ?? '') : value

const textSortValue = (value?: string | null): string | null => {
  const trimmed = value?.trim()
  return trimmed ? trimmed.toLowerCase() : null
}

const compareSortValues = (
  aValue: string | number | null,
  bValue: string | number | null,
  direction: DataTableSortDirection,
): number => {
  const multiplier = direction === 'asc' ? 1 : -1
  if (aValue === bValue) return 0
  if (aValue === null) return 1
  if (bValue === null) return -1
  return aValue < bValue ? -multiplier : multiplier
}

const faxStatusColor = (status: string): BadgeColor => {
  const key = status.trim().toLowerCase()
  if (
    key === 'active' ||
    key === 'ported in' ||
    key === 'completed' ||
    key === 'approved'
  ) {
    return 'vivid-green'
  }
  if (key === 'released' || key === 'failed' || key === 'cancelled') {
    return 'red'
  }
  if (key === 'pending') return 'yellow'
  return 'gray'
}

const getFaxSortValue = (
  row: FaxNumberRow,
  column: FaxSortColumn,
): string | null => {
  switch (column) {
    case 'name':
      return textSortValue(row.name)
    case 'fax_number':
      return textSortValue(row.fax_number)
    case 'assigned_to':
      return textSortValue(row.assigned_to)
    case 'status':
      return textSortValue(row.status)
  }
}

const FaxSettings = (): React.ReactElement => {
  const hooks = useFaxSettings()
  const [pendingRemove, setPendingRemove] = useState<{
    id: string
    numberDisplay: string
  } | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [sortColumnId, setSortColumnId] = useState<FaxSortColumn | null>(null)
  const [sortDirection, setSortDirection] =
    useState<DataTableSortDirection>('asc')

  const {
    setHeaderTitle,
    setHeaderButtons,
    hasChanges,
    loading,
    handleReset,
    handleSave,
    savedBaseline,
  } = hooks

  const isTrulyEmpty = !hooks.numbersLoading && hooks.faxNumbersCount === 0
  const leaveGuard = useCommunicationsLeaveGuard(hasChanges && hooks.dataReady)

  useEffect(() => {
    setHeaderButtons(
      <>
        <Button
          size="sm"
          type="button"
          variant="secondary"
          disabled={!hasChanges || loading || !hooks.dataReady}
          onClick={() => leaveGuard.requestLeave(handleReset)}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          type="button"
          variant="primary"
          disabled={!hasChanges || loading || !hooks.dataReady}
          onClick={() => {
            void handleSave()
          }}
        >
          Save changes
        </Button>
      </>,
    )
    return () => {
      setHeaderButtons(null)
    }
  }, [
    hasChanges,
    handleReset,
    handleSave,
    hooks.dataReady,
    leaveGuard.requestLeave,
    loading,
    setHeaderButtons,
    setHeaderTitle,
  ])

  useEffect(() => {
    setCurrentPage(1)
  }, [hooks.mainSearchQuery, hooks.mainFilters])

  const faxEntries = useMemo(
    () =>
      hooks.gridRows.map((row, index) => ({
        id: row.clinic_number_id || `fax-${index}-${row.fax_number}`,
        row,
      })),
    [hooks.gridRows],
  )
  const faxRowsById = useMemo(
    () => new Map(faxEntries.map((entry) => [entry.id, entry.row])),
    [faxEntries],
  )

  const handleSort = (columnId: string): void => {
    const column = columnId as FaxSortColumn
    if (sortColumnId !== column) {
      setSortColumnId(column)
      setSortDirection('asc')
    } else if (sortDirection === 'asc') {
      setSortDirection('desc')
    } else {
      setSortColumnId(null)
    }
    setCurrentPage(1)
  }

  const sortedEntries = useMemo(() => {
    if (!sortColumnId) return faxEntries
    return [...faxEntries].sort((left, right) =>
      compareSortValues(
        getFaxSortValue(left.row, sortColumnId),
        getFaxSortValue(right.row, sortColumnId),
        sortDirection,
      ),
    )
  }, [faxEntries, sortColumnId, sortDirection])

  const totalPages = Math.max(1, Math.ceil(sortedEntries.length / pageSize))
  const pageNumber = Math.min(currentPage, totalPages)
  const pageRows = useMemo(
    (): FaxTableRow[] =>
      sortedEntries
        .slice((pageNumber - 1) * pageSize, pageNumber * pageSize)
        .map((entry) => ({ id: entry.id })),
    [sortedEntries, pageNumber, pageSize],
  )

  const renderFaxRow =
    (render: (row: FaxNumberRow) => React.ReactNode) =>
    (row: FaxTableRow): React.ReactNode => {
      const faxRow = faxRowsById.get(row.id)
      return faxRow ? render(faxRow) : null
    }

  const columns: DataTableColumn<FaxTableRow>[] = [
    {
      id: 'name',
      label: 'Name',
      width: '25%',
      sortable: true,
      renderCell: renderFaxRow((row) => row.name),
    },
    {
      id: 'fax_number',
      label: 'Fax number',
      width: '25%',
      sortable: true,
      renderCell: renderFaxRow((row) => row.fax_number),
    },
    {
      id: 'assigned_to',
      label: 'Assigned to',
      width: '25%',
      sortable: true,
      renderCell: renderFaxRow((row) => row.assigned_to),
    },
    {
      id: 'status',
      label: 'Status',
      width: '15%',
      sortable: true,
      renderCell: renderFaxRow((row) =>
        row.status ? (
          <Badge
            mode="tinted"
            color={faxStatusColor(row.status)}
            label={row.status}
          />
        ) : (
          '—'
        ),
      ),
    },
  ]

  if (!hooks.dataReady) {
    return (
      <div className="scp-fax-container" id="settings-fax">
        {leaveGuard.modal}
        <SettingsSkeleton variant="table" pageKey="comm-fax" />
      </div>
    )
  }

  return (
    <div className="scp-fax-container" id="settings-fax">
      {leaveGuard.modal}
      <div className="scp-fc-filter">
        <span className="scp-fc-filter-title">Fax settings</span>
        <div className="scp-fc-fax-settings">
          <div className="scp-fc-toggle-item">
            <span>Add clinic logo on coversheet</span>
            <ToggleSwitch
              id="add-clinic-logo"
              aria-label="Add clinic logo on coversheet"
              checked={hooks.data.add_clinic_logo}
              onChange={(checked) =>
                hooks.handleChange('add_clinic_logo', checked)
              }
            />
          </div>
          <div className="scp-fc-toggle-item">
            <span>Show unread fax</span>
            <ToggleSwitch
              id="show-unread-fax"
              aria-label="Show unread fax"
              checked={hooks.data.show_unread}
              onChange={(checked) => hooks.handleChange('show_unread', checked)}
            />
          </div>
          <div className="scp-fc-toggle-item">
            <span>Notify on new fax</span>
            <ToggleSwitch
              id="notify-new-fax"
              aria-label="Notify on new fax"
              checked={hooks.data.notify_on_new_fax}
              onChange={(checked) =>
                hooks.handleChange('notify_on_new_fax', checked)
              }
            />
          </div>
          <div className="scp-fc-toggle-item">
            <span>Notify on failed delivery</span>
            <ToggleSwitch
              id="notify-failed"
              aria-label="Notify on failed delivery"
              checked={hooks.data.notify_on_failed_delivery}
              onChange={(checked) =>
                hooks.handleChange('notify_on_failed_delivery', checked)
              }
            />
          </div>
          <div className="scp-fc-toggle-item">
            <span>Resend failed faxes</span>
            <ToggleSwitch
              id="resend-failed-fax"
              aria-label="Resend failed faxes"
              checked={hooks.data.resend_failed_fax}
              onChange={(checked) =>
                hooks.handleChange('resend_failed_fax', checked)
              }
            />
          </div>
          <div />
          <InputWithUnit
            label="Fax retention period"
            placeholder="-"
            value={(hooks.data.retention_period || '').toString()}
            onChange={(value) => {
              if (hooks.data.retention_period_unit === 'No expiration') return
              hooks.handleChange('retention_period', Number(value))
            }}
            unit={hooks.data.retention_period_unit}
            units={RETENTION_UNITS.map((unit) => ({
              label: unit,
              value: unit,
            }))}
            onUnitChange={(unit) => {
              const next = typeof unit === 'string' ? unit : 'year/s'
              hooks.handleChange('retention_period_unit', next)
              if (next === 'No expiration') {
                hooks.handleChange('retention_period', 0)
              }
            }}
          />
          <InputField
            className={settingsFieldClassName(
              isSettingFieldChanged(
                hooks.data.max_pages ?? -1,
                savedBaseline.max_pages ?? -1,
              ),
              'scp-fax-max-pages',
            )}
            label="Maximum pages"
            variant="number"
            numberMin={0}
            placeholder="1"
            clearable={false}
            trailingIcon={<span className="scp-fax-pages-suffix">pages</span>}
            value={
              hooks.data.max_pages === null
                ? ''
                : hooks.data.max_pages.toString()
            }
            onChange={(event) => {
              const raw = event.target.value
              hooks.handleChange('max_pages', raw === '' ? null : Number(raw))
            }}
          />
          <div className="scp-fc-toggle-item">
            <Select
              className={settingsFieldClassName(
                isSettingFieldChanged(
                  hooks.data.show_images,
                  savedBaseline.show_images,
                ),
                'scp-fax-show-images',
              )}
              label="Show images"
              value={hooks.data.show_images}
              options={SHOW_IMAGE_OPTIONS}
              onChange={(value) => {
                hooks.handleChange('show_images', selectValue(value) || 'ask')
              }}
            />
          </div>
        </div>
      </div>

      <div className="scp-table-group">
        <div className="scp-table-title-container">
          <span className="scp-table-title">Assigned fax numbers</span>
          <div className="component-buttons">
            {!isTrulyEmpty ? (
              <SearchWithFilter
                placeholder="Search"
                value={hooks.mainSearchQuery}
                onChange={hooks.setMainSearchQuery}
                filters={{ status: hooks.statusFilterChoices }}
                filterLabels={{ status: 'Status' }}
                selectedFilters={hooks.mainFilters}
                onFilterChange={hooks.setMainFilters}
              />
            ) : null}
            {hooks.faxNumbersCount > 0 ? (
              <Select
                className="scp-add-number-select"
                label=""
                value={hooks.addMethod ?? ''}
                placeholder="Add number"
                options={[
                  { label: 'Purchase number', value: 'purchase' },
                  { label: 'Port number', value: 'port' },
                ]}
                onChange={(value) => hooks.setAddMethod(selectValue(value))}
              />
            ) : null}
          </div>
        </div>

        {!hooks.dataReady ? null : hooks.gridRows.length === 0 ? (
          <EmptyState
            className="scp-fax-empty"
            illustration={<FaxIcon className="empty-icon" />}
            title="No fax numbers found"
            description={
              hooks.faxNumbersCount === 0
                ? "You don't have any fax numbers on record yet."
                : 'No fax numbers match your search.'
            }
            actionLabel={hooks.faxNumbersCount === 0 ? 'Add number' : undefined}
            onAction={
              hooks.faxNumbersCount === 0
                ? () => hooks.setAddMethod('purchase')
                : undefined
            }
          />
        ) : (
          <DataTable<FaxTableRow>
            className="scp-fax-number-table"
            rows={pageRows}
            columns={columns}
            renderActions={renderFaxRow((row) => (
              <ActionButtons
                actions={[
                  {
                    id: 'delete',
                    icon: (
                      <DeleteFilledIcon
                        className="ds-action-button__svg"
                        aria-hidden="true"
                      />
                    ),
                    label: row.is_ported
                      ? 'Please contact EnamelAI to port out this number.'
                      : 'Delete',
                    tone: 'danger',
                    variant: 'outline',
                    disabled: !row.clinic_number_id || row.is_ported,
                    onClick: () => {
                      if (row.is_ported || !row.clinic_number_id) return
                      setPendingRemove({
                        id: row.clinic_number_id,
                        numberDisplay: row.fax_number,
                      })
                    },
                  },
                ]}
              />
            ))}
            sortColumnId={sortColumnId ?? undefined}
            sortDirection={sortColumnId ? sortDirection : undefined}
            onSort={handleSort}
            pagination={{
              currentPage: pageNumber,
              totalPages,
              pageSize,
              onPageChange: setCurrentPage,
              onPageSizeChange: (size) => {
                setPageSize(size)
                setCurrentPage(1)
              },
            }}
          />
        )}
      </div>

      {hooks.addMethod === 'purchase' && (
        <PurchaseFaxModal
          show={hooks.addMethod === 'purchase'}
          clinicId={hooks.clinicId}
          onHide={() => hooks.setAddMethod(null)}
          onAdded={() => {
            void hooks.refreshNumbers()
          }}
        />
      )}
      {hooks.addMethod === 'port' && (
        <PortFaxModal
          show={hooks.addMethod === 'port'}
          clinicId={hooks.clinicId}
          onHide={() => hooks.setAddMethod(null)}
          onAdded={() => {
            void hooks.refreshNumbers()
          }}
        />
      )}

      <FaxDeleteNumberModal
        show={pendingRemove !== null}
        faxNumber={pendingRemove?.numberDisplay ?? ''}
        onCancel={() => setPendingRemove(null)}
        onDelete={() => {
          if (!pendingRemove) return
          const id = pendingRemove.id
          void hooks.removeFaxNumber(id)
        }}
      />
    </div>
  )
}

export default FaxSettings
